import { CareTeam, Patient, Practitioner } from "@medplum/fhirtypes";

import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit";

import type { MessagePreview } from "@/messages/types/inbox";

import { RootState } from "..";

export interface CommunicationInbox {
  id: string;
  name: string;
  time: string | undefined;
  topic: string | undefined;
  status: string;
  recipient: Patient | Practitioner | CareTeam;
  unread: number;
  client: Patient | undefined;
  /** The thread's latest message, absent when the thread has no messages or the list didn't load it. */
  lastMessagePreview?: MessagePreview;
}

/** Orders threads newest message first, with threads that have no time last. */
export function compareInboxByLatestMessage(a: CommunicationInbox, b: CommunicationInbox): number {
  if (!a.time && !b.time) return 0;
  if (!a.time) return 1;
  if (!b.time) return -1;

  return new Date(b.time).getTime() - new Date(a.time).getTime();
}

const inboxAdapter = createEntityAdapter({
  selectId: (inbox: CommunicationInbox) => inbox.id,
});

// Tracks threads added locally via `addToInbox` that no `loadInbox` snapshot has confirmed yet, so
// a full sync can tell "not in this snapshot because it's brand new" apart from "not in this
// snapshot because it's gone" (closed, or from a practice the user has since switched away from).
type InboxState = EntityState<CommunicationInbox, string> & { pendingIds: string[] };

const initialState: InboxState = inboxAdapter.getInitialState({ pendingIds: [] as string[] });

const reducers = {
  // A full, authoritative sync of the current practice's inbox: replaces the collection with the
  // fetched snapshot so closed/removed threads and threads from a previous practice don't linger,
  // while re-adding any thread still pending confirmation so a fetch that predates it (the
  // statement hand-off / "new message" modal race) doesn't wipe it back out.
  //
  // A pending thread is protected through exactly one loadInbox cycle, not indefinitely: this
  // reducer can't tell "missing because this snapshot predates the thread's creation" apart from
  // "missing because the thread is now genuinely gone," so once a thread has survived one miss
  // unconfirmed, it stops being treated as pending — the predates race resolves on the very next
  // full sync anyway, and protecting it forever would turn a thread closed/removed before ever
  // being confirmed into a permanent phantom entry.
  loadInbox: (state: InboxState, action: PayloadAction<CommunicationInbox[]>) => {
    const pendingEntities = state.pendingIds
      .map((id) => state.entities[id])
      .filter((entity): entity is CommunicationInbox => Boolean(entity));

    inboxAdapter.setAll(state, action.payload);

    for (const entity of pendingEntities) {
      if (!state.entities[entity.id]) {
        inboxAdapter.addOne(state, entity);
      }
    }
    state.pendingIds = [];
  },
  addToInbox: (state: InboxState, action: PayloadAction<CommunicationInbox>) => {
    inboxAdapter.addOne(state, action.payload);
    state.pendingIds.push(action.payload.id);
  },
  removeFromInbox: (state: InboxState, action: PayloadAction<string>) => {
    inboxAdapter.removeOne(state, action.payload);
    state.pendingIds = state.pendingIds.filter((id) => id !== action.payload);
  },
  updateInbox: inboxAdapter.updateOne,
  upsertInbox: inboxAdapter.upsertOne,
};

export const inboxSlice = createSlice({
  name: "inbox",
  initialState,
  reducers,
});

export const { loadInbox, addToInbox, removeFromInbox, updateInbox, upsertInbox } =
  inboxSlice.actions;

export const selectCommunicationInboxEntities = (
  state: RootState,
): Record<string, CommunicationInbox> => state.inbox.entities;

export const selectCommunicationInboxList = createSelector(
  [selectCommunicationInboxEntities],
  (inboxEntities): CommunicationInbox[] =>
    Object.values(inboxEntities).sort(compareInboxByLatestMessage),
);

export const selectFirstCommunicationInbox = createSelector(
  [selectCommunicationInboxList],
  (inboxItems): CommunicationInbox | undefined => {
    return inboxItems[0];
  },
);

export const selectCommunicationInbox = createSelector(
  [selectCommunicationInboxEntities, (_: RootState, id: string) => id],
  (inboxEntities, id): CommunicationInbox | undefined => {
    return inboxEntities[id];
  },
);

export const selectUnreadCommunicationsCount = createSelector(
  [selectCommunicationInboxEntities],
  (inboxEntities): number => {
    return Object.values(inboxEntities).reduce((acc, inbox) => acc + inbox.unread, 0);
  },
);

export default inboxSlice.reducer;
